import { useCookieConsent } from '~/composables/useCookieConsent'

type VitalMetric = {
  name: 'CLS' | 'INP' | 'LCP' | 'FCP' | 'TTFB'
  value: number
  delta: number
  id: string
}

const pendingMetrics: VitalMetric[] = []
let consentWatcherStarted = false

const hasAnalyticsConsent = (): boolean => {
  if (typeof window === 'undefined') return false

  try {
    const cmpkit = (window as any).cmpkit
    if (cmpkit && typeof cmpkit.getConsent === 'function') {
      const consent = cmpkit.getConsent()
      if (consent && typeof consent.analytics === 'boolean') {
        return consent.analytics === true
      }
    }
    if (cmpkit && typeof cmpkit.hasConsent === 'function') {
      return cmpkit.hasConsent('analytics') === true
    }
  } catch {
    // Fall through to composable/cookie fallback.
  }

  try {
    const { getConsent } = useCookieConsent()
    return getConsent().analytics === true
  } catch {
    return false
  }
}

const sendToGA = (metric: VitalMetric) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return
  if (!hasAnalyticsConsent()) return

  window.gtag('event', 'web_vitals', {
    metric_name: metric.name,
    metric_value: Math.round(metric.value),
    metric_id: metric.id,
    metric_delta: metric.delta,
    non_interaction: true
  })
}

const sendToDataLayer = (metric: VitalMetric) => {
  if (typeof window === 'undefined') return
  if (!hasAnalyticsConsent()) return

  const dataLayer = ((window as any).dataLayer = (window as any).dataLayer || [])
  dataLayer.push({
    event: 'web_vitals',
    metric_name: metric.name,
    metric_value: Math.round(metric.value),
    metric_id: metric.id,
    metric_delta: metric.delta
  })
}

const sendAnalyticsMetrics = (metric: VitalMetric) => {
  if (!hasAnalyticsConsent()) {
    // Keep bounded queue to avoid unbounded growth on long sessions.
    if (pendingMetrics.length >= 50) pendingMetrics.shift()
    pendingMetrics.push(metric)
    return
  }

  sendToDataLayer(metric)
  sendToGA(metric)
}

const sendSignalsMetric = (metric: VitalMetric) => {
  sendToSignalsKit(metric)
}

const flushPendingMetrics = () => {
  if (!hasAnalyticsConsent() || pendingMetrics.length === 0) return
  const queued = pendingMetrics.splice(0, pendingMetrics.length)
  queued.forEach((metric) => {
    sendSignalsMetric(metric)
    sendToDataLayer(metric)
    sendToGA(metric)
  })
}

const startConsentWatcher = () => {
  if (consentWatcherStarted || typeof window === 'undefined') return
  consentWatcherStarted = true

  const tryFlush = () => flushPendingMetrics()
  setInterval(tryFlush, 1000)
  window.addEventListener('focus', tryFlush)
}

const sendToSignalsKit = (metric: VitalMetric) => {
  if (typeof window === 'undefined') return
  const tracker = (window as any).signalsKit
  if (!tracker || typeof tracker.track !== 'function') return

  const normalizedValue = metric.name === 'CLS'
    ? Math.round(metric.value * 1000)
    : Math.round(metric.value)

  tracker.track('web_vital', {
    // Keep top-level fields for backward compatibility.
    metric_name: metric.name,
    name: metric.name,
    metric: metric.name,
    metric_id: metric.id,
    metric_value: metric.value,
    metric_delta: metric.delta,
    value: normalizedValue,
    // Some dashboards segment on event_properties.* keys.
    event_properties: {
      metric_name: metric.name,
      name: metric.name,
      metric: metric.name,
      metric_id: metric.id,
      metric_value: metric.value,
      metric_delta: metric.delta,
      value: normalizedValue
    }
  })
}

const reportMetric = (metric: VitalMetric) => {
  if (!hasAnalyticsConsent()) {
    if (pendingMetrics.length >= 50) pendingMetrics.shift()
    pendingMetrics.push(metric)
    return
  }

  sendSignalsMetric(metric)
  sendAnalyticsMetrics(metric)
}

export default defineNuxtPlugin(async () => {
  if (!process.client) return

  startConsentWatcher()

  const { onCLS, onINP, onLCP, onFCP, onTTFB } = await import('web-vitals')

  onCLS(reportMetric)
  onINP(reportMetric)
  onLCP(reportMetric)
  onFCP(reportMetric)
  onTTFB(reportMetric)
})
